import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Table, RowActions, InfoIcon, StatusPill, Container } from 'shared-portal-component';

import type { UserRow } from '@/types/users.types';
import { OrgUserStatus } from '@/types/status.types';
import '@/components/organism/Users/UsersTable/UsersTable.scss';

interface UsersTableProps {
  rows: UserRow[];
  loading?: boolean;
  onEdit: (row: UserRow) => void;
  onToggleActive: (row: UserRow) => void;
  onForceLogout: (row: UserRow) => void;
  onForceLoginReset: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
  /** Whether transitioning this row to Active is allowed by the seat limit. */
  canActivate: (row: UserRow) => boolean;
}

const UsersTable: React.FC<UsersTableProps> = ({
  rows,
  loading,
  onEdit,
  onToggleActive,
  onForceLogout,
  onForceLoginReset,
  onDelete,
  canActivate,
}) => {
  const COLUMNS: Array<ColumnDef<UserRow>> = [
    { accessorKey: 'name', header: 'User' },
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'phone', header: 'Phone' },
    { accessorKey: 'deptOrg', header: 'Dept / Org' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue() as UserRow['status'];
        return <StatusPill status={status} />;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const { original } = row;
        const actions = [
          {
            label: 'Edit User',
            icon: <InfoIcon size="1x" />,
            onClick: () => {
              onEdit(original);
            },
          },
          ...(original.status !== OrgUserStatus.Unregistered &&
          (original.status === OrgUserStatus.Active || canActivate(original))
            ? [
                {
                  label: original.status === OrgUserStatus.Active ? 'Make Inactive' : 'Make Active',
                  icon: <InfoIcon size="1x" />,
                  onClick: () => {
                    onToggleActive(original);
                  },
                },
              ]
            : []),
          {
            label: 'Force Logout',
            icon: <InfoIcon size="1x" />,
            onClick: () => {
              onForceLogout(original);
            },
          },
          {
            label: 'Force Login Reset',
            icon: <InfoIcon size="1x" />,
            onClick: () => {
              onForceLoginReset(original);
            },
          },
          {
            label: 'Delete User',
            icon: <InfoIcon size="1x" />,
            onClick: () => {
              onDelete(original);
            },
          },
        ];
        return <RowActions actions={actions} />;
      },
    },
  ];

  return (
    <Container fluid className="users-table">
      <Table<UserRow> data={rows} columns={COLUMNS} variant="admin" size="sm" loading={loading} />
    </Container>
  );
};

export default UsersTable;
